import api from './api'

/**
 * WebMCP (Web Model Context Protocol) — registruje „nástroje" pro AI agenty v prohlížeči,
 * aby uměli spolehlivě spustit naše klíčové akce (SEO analýza, jednotlivé nástroje).
 * W3C Community Draft (2026), `navigator.modelContext.registerTool` — Chrome 146+/Edge 147+.
 * V prohlížečích bez WebMCP je to no-op (feature detection). Deklarativní HTML anotace
 * jsou zatím ve specifikaci odložené, takže používáme imperativní API.
 */
export function registerWebMcpTools(): void {
  const mc = (typeof navigator !== 'undefined' ? (navigator as any).modelContext : null)
  if (!mc || typeof mc.registerTool !== 'function') return // prohlížeč bez WebMCP → nic

  const safe = (fn: () => void) => { try { fn() } catch { /* už registrováno / nepodporováno */ } }

  // 1) Kompletní on-page SEO analýza URL
  safe(() => mc.registerTool({
    name: 'analyze_website_seo',
    description: 'Spustí kompletní on-page SEO analýzu zadané webové stránky a vrátí skóre 0–100 a hlavní zjištění (title, meta description, nadpisy, obrázky, výkon, strukturovaná data a další).',
    inputSchema: {
      type: 'object',
      properties: {
        url: { type: 'string', description: 'Úplná URL stránky k analýze, např. https://example.com' },
      },
      required: ['url'],
    },
    annotations: { readOnlyHint: true },
    execute: async (input: any) => {
      const { data } = await api.post('/analyze', { url: String(input?.url || ''), public: false })
      if (!data || data.error) return { error: data?.message || 'Analýza selhala.' }
      const results = data.results || {}
      const issues = Object.entries(results)
        .filter(([, v]: [string, any]) => v?.status && v.status !== 'pass')
        .map(([k, v]: [string, any]) => ({ check: k, status: v.status, message: v.message }))
      return {
        url: data.url, domain: data.domain, score: data.score, max_score: 100,
        issues_count: issues.length, top_issues: issues.slice(0, 8),
      }
    },
  }))

  // 2) Spuštění konkrétního SEO/GEO nástroje
  safe(() => mc.registerTool({
    name: 'run_seo_tool',
    description: 'Spustí konkrétní SEO nebo GEO nástroj na zadané URL: robots.txt, XML sitemap, strukturovaná data (schema), mobilní přívětivost, bezpečnostní hlavičky, rychlost (PageSpeed/Lighthouse), přístupnost (WCAG), cookies/GDPR, DNS a SSL, nefunkční odkazy nebo GEO (optimalizace pro AI vyhledávače).',
    inputSchema: {
      type: 'object',
      properties: {
        tool: {
          type: 'string',
          description: 'Který nástroj spustit',
          enum: ['robots', 'sitemap', 'schema', 'serp', 'hreflang', 'mobile', 'security', 'pagespeed', 'lighthouse', 'redirects', 'content', 'technology', 'accessibility', 'wcag', 'cookies', 'dns', 'broken-links', 'geo'],
        },
        url: { type: 'string', description: 'Úplná URL stránky, např. https://example.com' },
      },
      required: ['tool', 'url'],
    },
    annotations: { readOnlyHint: true },
    execute: async (input: any) => {
      const { data } = await api.post(`/tools/${String(input?.tool || '')}`, { url: String(input?.url || '') })
      return data ?? { error: 'Nástroj nevrátil data.' }
    },
  }))
}
